import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Calculator, ScanSearch, Search, ShieldCheck } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { absoluteUrl, breadcrumbJsonLd, ldScript, pageHead } from "@/lib/seo";

const PATH = "/lookup-validators";
const TITLE = "Barcode Lookup & Validators — UPC, EAN, ISBN, GTIN";
const DESC =
  "Look up UPC and EAN barcodes, validate ISBN and GTIN check digits, calculate check digits and identify the format of any barcode value. Free, no signup.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Lookup & Validators", path: PATH },
];

type LookupCard = {
  path: string;
  label: string;
  tagline: string;
  description: string;
  icon: typeof Search;
  formats: string[];
};

const TOOLS: LookupCard[] = [
  {
    path: "/upc-lookup",
    label: "UPC Lookup",
    tagline: "12-digit retail codes",
    description:
      "Validate any UPC-A or UPC-E barcode, check the trailing check digit, and decode the number system digit. Use this for the 12-digit barcodes on US and Canadian retail packaging.",
    icon: Search,
    formats: ["UPC-A", "12 digits", "Check digit"],
  },
  {
    path: "/ean-lookup",
    label: "EAN Lookup",
    tagline: "European retail codes",
    description:
      "Validate EAN-13 (13 digits) and EAN-8 (8 digits) — the global retail standard outside North America. Confirms the check digit and the GS1 country / publisher prefix.",
    icon: Search,
    formats: ["EAN-13", "EAN-8", "GS1 prefix"],
  },
  {
    path: "/isbn-barcode-reader",
    label: "ISBN Barcode Reader",
    tagline: "Books & publishing",
    description:
      "Validate a typed ISBN-10 or ISBN-13. Confirms the check digit, converts between ISBN-10 and ISBN-13 and explains each part of the number.",
    icon: BookOpen,
    formats: ["ISBN-10", "ISBN-13", "EAN-13"],
  },
  {
    path: "/gtin-validator",
    label: "GTIN Validator",
    tagline: "Universal product identifier",
    description:
      "Validate GTIN-8, GTIN-12 (UPC-A), GTIN-13 (EAN-13) and GTIN-14 (ITF-14) in one place. Verifies the GS1 mod-10 check digit and suggests the correct one when it is wrong.",
    icon: ShieldCheck,
    formats: ["GTIN-8 / 12 / 13 / 14", "Mod-10", "Normalise"],
  },
  {
    path: "/check-digit-calculator",
    label: "Check Digit Calculator",
    tagline: "Mod-10 in one step",
    description:
      "Paste a UPC, EAN or GTIN body (without the last digit) and get the correct GS1 check digit. Handy when building barcodes from a partial sequence or fixing a typo.",
    icon: Calculator,
    formats: ["UPC", "EAN", "GTIN-14"],
  },
  {
    path: "/barcode-type-detector",
    label: "Barcode Type Detector",
    tagline: "Identify unknown values",
    description:
      "Paste an unknown barcode value and the detector tells you the most likely symbology (UPC-A, EAN-13, GTIN-14, ISBN, GS1 Digital Link, GS1-128 …) plus what to do next.",
    icon: ScanSearch,
    formats: ["1D + 2D", "GS1", "ISBN"],
  },
];

const FAQ = [
  {
    q: "What's the Difference Between a UPC and an EAN?",
    a: "UPC-A is 12 digits and dominant in the US and Canada. EAN-13 is 13 digits and is the worldwide standard everywhere else — including the UK and EU. The two are interchangeable in modern scanners; UPC-A is effectively an EAN-13 with a leading zero. See the UPC vs EAN guide for a deeper comparison.",
  },
  {
    q: "Do These Lookups Go to an External Product Database?",
    a: "No. Every tool runs in your browser. The lookups validate structure (length, prefix, check digit) and surface GS1 metadata that the value itself encodes; we do not call any third-party product database. If you need product names, pair the validated GTIN with the retailer / wholesaler database you're licensed for.",
  },
  {
    q: "Is the Check Digit the Same Algorithm for UPC, EAN, ISBN-13 and GTIN-14?",
    a: "UPC, EAN-13 and GTIN-8/12/13/14 all use the same GS1 mod-10 algorithm — that's why the GTIN Validator handles them in one tool. ISBN-13 is also GS1 mod-10 (it's literally an EAN-13 with the 978/979 prefix). ISBN-10 uses a different mod-11 algorithm with X as a possible check character; the ISBN reader handles both.",
  },
  {
    q: "What Is the Check Digit Calculator for If the Validators Already Compute It?",
    a: "Use the validator when you have a complete barcode and want to confirm it's correct. Use the calculator when you have the first N digits (a partial body, often from a planning spreadsheet or label artwork) and need the final digit to make a real, scannable code.",
  },
  {
    q: "Why Would I Use the Barcode Type Detector?",
    a: "You scanned, OCR'd or were sent a raw value but don't know which symbology it came from. The detector heuristically classifies the string (length, character set, GS1 markers, ISBN prefix) so you can route it to the right validator or lookup without guessing.",
  },
  {
    q: "Are My Entered Barcodes Logged or Sent Anywhere?",
    a: "No. There is no server-side storage and no analytics on the values you paste — the tools are pure browser code. See the Privacy page for details.",
  },
  {
    q: "Does a Valid Barcode Number Mean the Product Exists?",
    a: "No. A matching check digit proves only that the number is well formed; it says nothing about whether a company was assigned it or whether any product carries it. To see who a GTIN is licensed to, search it on Verified by GS1. The tools on this page never contact a product database.",
  },
  {
    q: "Is There a Barcode Validator App?",
    a: "You do not need to install one: every validator on this page runs in a mobile browser. On a phone, scan the code with the online barcode reader, then paste the digits into the matching validator. Nothing you enter leaves the device.",
  },
];

function ValiditySections() {
  return (
    <>
      <h2>How to Check If a Barcode Is Valid</h2>
      <p>A barcode number is valid when its length fits the format and its last digit matches the check digit calculated from the digits before it.</p>
      <ol>
        <li>Read the digits printed under the bars, or scan the code with the <Link to="/barcode-reader">barcode reader</Link> to get them exactly.</li>
        <li>Count them: 8 for EAN-8, 12 for UPC-A, 13 for EAN-13 or ISBN-13, 14 for GTIN-14, and 10 for ISBN-10.</li>
        <li>Paste the value into the validator for that format, or into the <Link to="/gtin-validator">GTIN validator</Link>, a GS1 barcode validator for all four GTIN lengths, if you are not sure which one you have.</li>
        <li>If the check fails, look for a mistyped or swapped digit before you print or list the code.</li>
      </ol>
      <p>This works as a barcode validity checker for any barcode with a check digit in its number. The <Link to="/barcode-guides/check-digits-explained">check digits guide</Link> walks through the mod-10 and mod-11 arithmetic. A UPC-A and the EAN-13 formed by adding a leading zero share one check digit, as the <Link to="/barcode-guides/upc-vs-ean">UPC vs EAN comparison</Link> shows.</p>
      <h3>Barcode Validation vs Barcode Verification</h3>
      <p>Validation checks the number; verification grades the printed symbol with a barcode verifier, following ISO/IEC 15416 for linear barcodes and ISO/IEC 15415 for 2D symbols.</p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Aspect</th>
              <th scope="col">Validation</th>
              <th scope="col">Verification</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>What it checks</td>
              <td>Length, prefix and check digit</td>
              <td>Contrast, edges, defects and decodability of the print</td>
            </tr>
            <tr>
              <td>Tool</td>
              <td>Software such as these validators</td>
              <td>A calibrated barcode verifier</td>
            </tr>
            <tr>
              <td>Result</td>
              <td>Pass or fail</td>
              <td>A grade from 4.0 (A) down to 0.0 (F)</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
      <p>A number can pass validation and the printed barcode still fail verification because of low contrast or a symbol printed too small. The GS1 General Specifications set a minimum grade of 1.5 (C) for most retail EAN/UPC symbols.</p>
    </>
  );
}

export const Route = createFileRoute("/lookup-validators")({
  component: LookupValidatorsPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESC,
          url: absoluteUrl(PATH),
          hasPart: TOOLS.map((t) => ({
            "@type": "SoftwareApplication",
            name: t.label,
            description: t.description,
            url: absoluteUrl(t.path),
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Any (Web)",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          })),
        }),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function LookupValidatorsPage() {
  return (
    <ToolLayout
      eyebrow="Hub"
      title="Barcode Lookup & Validators"
      description="Validate UPC, EAN, ISBN and GTIN check digits, calculate the correct check digit from a partial value, and detect the likely format of an unknown barcode — all client-side. The UPC and EAN lookups also decode the number system digit or the GS1 country prefix, and the ISBN reader converts between ISBN-10 and ISBN-13."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the Lookup & Validator Tools"
            about={
              <>
                <p>
                  These tools cover the boring-but-essential second half of a barcode workflow:
                  confirming that the value you scanned, OCR&apos;d or were sent is a real, well-formed
                  identifier — and finishing the value when it&apos;s incomplete. Every tool runs in
                  your browser. There is no third-party product database call: we validate the structure
                  (length, character set, country prefix, mod-10 check digit) that the value itself
                  encodes.
                </p>
                <p>
                  Need to look up actual product data behind a verified GTIN? Pair the validator with
                  the wholesaler / retailer feed you&apos;re already licensed for. Need to <Link to="/barcode-reader">read a barcode</Link> first, or <Link to="/generators">generate a new one</Link> from a verified value? The reader and generator hubs have you covered.
                </p>
              </>
            }
            whenToUse={[
              "Confirming a 12- or 13-digit barcode from a supplier feed before sending it to print.",
              "Validating an ISBN supplied by an author or distributor against the GS1 mod-10 / ISO 2108 mod-11 rules.",
              "Computing the final check digit for a partial GTIN body you pulled out of a planning spreadsheet.",
              "Triaging an unknown barcode value pulled from a CSV export and figuring out which symbology to expect.",
              "Cross-checking a UPC-A on a US retail item against the same product's EAN-13 export listing.",
              "Round-trip-verifying a barcode you just generated — read it back and validate the value with the matching tool.",
            ]}
            howItWorks={[
              {
                title: "Paste a value",
                desc: "Each tool accepts the raw digits from a scan, a screenshot OCR, a supplier feed or a label-artwork file.",
              },
              {
                title: "Pick the right tool",
                desc: "Use the detector if you don't know the format; a format-specific lookup (UPC / EAN / ISBN) when you do; the GTIN validator for cross-format.",
              },
              {
                title: "Read the verdict",
                desc: "Tools return validity (pass / fail), the canonical normalised form, the GS1 prefix where it applies, and a one-click copy of the corrected value.",
              },
              {
                title: "Fix or finish",
                desc: "If the check digit is wrong, the Check Digit Calculator gives you the right one. If the format is wrong, the detector tells you what it actually looks like.",
              },
              {
                title: "Hand off",
                desc: "Take the validated value into a generator, a retailer feed or your inventory system. The tools never store the values you paste.",
              },
            ]}
            whatToAvoid={[
              "Don't treat a valid check digit as proof that a product exists — these tools verify structure, not ownership or retail registration.",
              "Don't strip leading zeros from a UPC-A before validating — the leading zero is one of the 12 required digits.",
              "Don't pass a GS1 Digital Link URL into the UPC / EAN lookups — use the GS1 Digital Link Decoder instead; the raw URL won't validate as a 12- or 13-digit barcode.",
              "Don't enter ISBN-10 values into the EAN lookup; ISBN-13 (978 / 979 prefix) is the EAN-13 form — ISBN-10 needs the ISBN reader's mod-11 check.",
              "Don't assume the Type Detector is authoritative for short numeric values — a 12-digit string could be UPC-A, GTIN-12 or coincidence; verify with the matching validator.",
            ]}
            tips={[
              "When triaging a batch of unknown barcodes, run them through the Type Detector first, then route to the matching validator — much faster than guessing per value.",
              "If the GTIN Validator flags a check-digit mismatch, the Check Digit Calculator will show you what the correct final digit should be, often surfacing a single transposed digit upstream.",
              "ISBN-13 always starts with 978 or 979 — if you see a 13-digit barcode without that prefix, it's an EAN-13 for a non-book product, not an ISBN.",
              "GTIN-14 with a leading 0 is just a GTIN-13 (EAN-13) padded with a zero on the left, so both forms share the same check digit.",
              "Always pair a generator with the matching validator in your QA — generate a code, read it back, validate the value, then sign off the artwork.",
            ]}
          />
          <ValiditySections />
          <FaqSection items={FAQ} />
        </>
      }
      related={
        <section className="mt-12">
          <h2 className="text-xl font-semibold tracking-tight md:text-2xl">
            Related Tools and Guides
          </h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              {
                to: "/barcode-reader",
                label: "Online Barcode Reader",
                desc: "Scan a barcode from camera or image and feed the result into a validator on this page.",
              },
              {
                to: "/generators",
                label: "Barcode & QR Code Generators",
                desc: "Generate UPC, EAN, QR, Data Matrix and GS1 Digital Link from a validated value.",
              },
              {
                to: "/gs1-inventory",
                label: "GS1 & Inventory Tools",
                desc: "Decode GS1 Digital Link URLs and manage barcode inventory lists with CSV export.",
              },
              {
                to: "/barcode-guides/what-is-a-gtin",
                label: "What is a GTIN?",
                desc: "Plain-English guide to GTIN-8/12/13/14 and the GS1 mod-10 check digit.",
              },
              {
                to: "/barcode-guides/upc-vs-ean",
                label: "UPC vs EAN",
                desc: "When you'll see each format, and how to convert between them.",
              },
              {
                to: "/barcode-guides/check-digits-explained",
                label: "Check Digits Explained",
                desc: "The mod-10 and mod-11 arithmetic behind every validator on this page.",
              },
              {
                to: "/barcode-guides/barcode-formats",
                label: "Barcode formats field guide",
                desc: "Quick-reference table of common 1D and 2D symbologies and their valid value patterns.",
              },
            ].map((t) => (
              <Link
                key={t.to}
                to={t.to}
                className="group flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-4 transition hover:border-primary/50 hover:bg-muted/40"
              >
                <div>
                  <div className="text-sm font-semibold">{t.label}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
                </div>
                <ArrowRight
                  className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden
                />
              </Link>
            ))}
          </div>
        </section>
      }
    >
      <section aria-label="Lookup and validator tools">
        <div className="grid gap-4 sm:grid-cols-2">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.path}
                to={t.path}
                className="group flex h-full flex-col rounded-lg border border-border bg-background p-5 transition hover:border-primary/50 hover:bg-muted/30"
              >
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  {t.tagline}
                </div>
                <h2 className="mt-3 text-lg font-semibold leading-snug group-hover:text-primary">
                  {t.label}
                </h2>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{t.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {t.formats.map((f) => (
                    <span
                      key={f}
                      className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Open tool
                  <ArrowRight
                    className="h-3.5 w-3.5 transition group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </ToolLayout>
  );
}
